import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus } from 'react-feather'
import { Badge, Button } from 'reactstrap'
import { mutations, queries } from '../../../../../api'
import { ImagePath } from '../../../../../constants'
import { KEYS } from '../../../../../constants/keys'
import { TAB_TO_SCREEN_MAP } from '../../../../../data/components'
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks'
import {
  selectChat,
  setIsNewMessage,
  setIsUnread,
  setOnSelect,
  setRecentChats,
  setRecentChatsLoading,
  setSelectedChatMessages,
  setSelectedUser,
  togglePinChat,
} from '../../../../../redux/reducers/messenger/chatSlice'
import { setMainSidebarActiveTab } from '../../../../../redux/reducers/messenger/mainSidebarSlice'
import { openCloseSidebar, setMobileMenu } from '../../../../../redux/reducers/messenger/messengerSlice'
import { setScreen } from '../../../../../redux/reducers/messenger/screenSlice'
import { setChatPinVerified } from '../../../../../redux/reducers/userSettingSlice'
import RecentChatsSkeleton from '../../../../../shared/skeleton-loader/RecentChatsSkeleton'
import { SvgIcon } from '../../../../../shared/icons'
import { Image } from '../../../../../shared/image'
import ChatAvatar from '../../../../../shared/image/ChatAvatar'
import PresenceProfile from '../../../../../shared/PresenceProfile'
import { RecentChat, RecentChatsProps } from '../../../../../types/components/chat'
import { formatTime } from '../../../../../utils'
import LockChatModal from '../../../chat/chat-area/chat-header/contact/LockChatModal'
import DecryptedMessagePreview from './DecryptedMessagePreview'
import RecentQuickAction from './RecentQuickAction'
import '../../../../../utils/prototype'

const LONG_PRESS_DURATION = 500 // ms

const RecentChats: React.FC<RecentChatsProps> = ({ onChatSelect, localSearch }) => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const [lockOpen, setLockOpen] = useState(false)
  const {
    selectedChat,
    selectedUser,
    recentChats: rawRecentChats,
    isNewMessage,
    recentChatsLoading,
  } = useAppSelector((state) => state.chat)

  // Use recentChats from Redux
  const recentChats = rawRecentChats
  const { subRecentActiveTab } = useAppSelector((state) => state.messenger)
  const { profileSidebarWidth, globalSearchTerm, subChatActiveTab } = useAppSelector((state) => state.messenger)
  const { time_format } = useAppSelector((state) => state.settings)
  const use12HourFormat = time_format === '12h'
  const { app_name } = useAppSelector((state) => state.settings)
  const { chatPinVerified } = useAppSelector((state) => state.userSettings)
  // `globalSearchTerm` is already debounced in ChatTitle before it is dispatched.
  // Debouncing it a second time here delayed the query and desynced it from the
  // display condition (which reads globalSearchTerm), so the first search could
  // show nothing until you typed again — notably on Opera. Use it directly.
  const [, setPinedMenu] = useState<{ [key: number | string]: boolean }>({})
  const [activeDropdown, setActiveDropdown] = useState<RecentChat | null>(null)
  // Timestamp until which row clicks are ignored. iOS Safari fires a synthesized
  // "ghost click" on the chat row beneath the menu right after a menu tap; the
  // quick-action dropdown is portaled to #dropdown-root (outside this row), so
  // that ghost click can't be stopped via propagation. Suppress row clicks for a
  // short window after any menu interaction instead.
  const ignoreRowClickUntilRef = useRef<number>(0)
  const count = useRef<number>(0)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressTriggeredRef = useRef(false)
  const { data: currentUserData } = queries.useGetUserDetails()
  const { data: searchRecent } = queries.useSearchRecent(globalSearchTerm, subChatActiveTab)
  const { isChatList } = useAppSelector((state) => state.templateCustomizer)
  const { data: recentChatsData, refetch } = queries.useGetRecentChats()
  const hasMountedRef = useRef(false)
  const expectedCountRef = useRef(recentChats?.length || 0)
  const [stableRecentChats, setStableRecentChats] = useState(recentChats)

  // Stabilize the list to prevent rendering temporary count spikes.
  // On the full Messages page, guard against non-refetch count increases
  // (e.g. socket-triggered addOrUpdateRecentChat during a message send can
  // temporarily push 25→26→25 before the server refetch resolves the duplicate).
  // In the sticky CHAT_LIST widget this guard is intentionally skipped so that
  // legitimately added contacts (e.g. upsertRecentChat when starting a new chat
  // from a connections/profile page) appear immediately without waiting for a
  // server refetch — the server doesn't include the contact until a message has
  // been sent, so bypassing the guard is the only way to show it right away.
  useEffect(() => {
    const newCount = recentChats?.length || 0
    const expected = expectedCountRef.current

    // In sticky mode always accept — see comment above.
    // On Messages page, only block if the increase is NOT coming from a server
    // refetch (recentChatsLoading transitions true→false as a pair with
    // setRecentChats, but React 18 batches them so loading is false here).
    // Instead we track whether the last setRecentChats was from the server by
    // updating expectedCountRef whenever recentChatsData changes — any count
    // increase after a server fetch is legitimate and must pass through.
    if (!isChatList && newCount > expected && !recentChatsLoading && expected > 0) {
      return
    }

    setStableRecentChats(recentChats)
    expectedCountRef.current = newCount
  }, [recentChats, recentChatsLoading, isChatList])

  // Use stabilized list for rendering
  const recentChatsList = useMemo(() => stableRecentChats ?? [], [stableRecentChats])

  // Fetch recent chats on initial mount (for left sidebar)
  useEffect(() => {
    if (!isChatList) {
      refetch()
    }
    hasMountedRef.current = true
  }, [])

  // Refetch when a new message arrives via socket
  useEffect(() => {
    if (!hasMountedRef.current) return
    if (isNewMessage && !!recentChats) {
      refetch()
      dispatch(setRecentChatsLoading(false))
      dispatch(setIsNewMessage(false))
    }
  }, [isNewMessage])

  useEffect(() => {
    dispatch(setRecentChatsLoading(true))
  }, [])

  useEffect(() => {
    if (!recentChatsData) return
    // Update the baseline before dispatching so the stabilizer's expected count
    // matches the server response. This ensures a server-returned list that is
    // larger than the previous client-side count (e.g. a new contact that just
    // had their first message sent) is NOT treated as a "temporary spike" and
    // blocked from rendering.
    expectedCountRef.current = recentChatsData.chats?.length ?? 0
    dispatch(setRecentChats(recentChatsData.chats ?? []))
    dispatch(setRecentChatsLoading(false))
  }, [dispatch, recentChatsData])

  // Note: removed local state sync - now using useMemo directly from Redux state

  const filterByTab = (chats?: RecentChat[]) => {
    if (!chats) return []

    switch (subRecentActiveTab) {
      case 'all':
        return chats.filter((contact) => !contact.isArchived && !contact.isLocked)

      case 'unread':
        return chats.filter((contact) => !contact.isArchived && contact.unreadCount > 0)

      case 'favourite':
        return chats.filter((contact) => !contact.isArchived && contact.isFavorite)

      case 'group':
        return chats.filter((contact) => contact.chat_type == 'group')

      case 'lock_chat':
        return chats.filter((contact) => contact.isLocked)

      default:
        return chats.filter((contact) => !contact.isArchived)
    }
  }

  const visibleRecentChats = filterByTab(recentChatsList)
  let displayData = visibleRecentChats
  if (localSearch) {
    displayData =
      visibleRecentChats?.filter((contact) => contact.name?.toLowerCase().includes(localSearch.toLowerCase())) || []
  } else if (searchRecent && subChatActiveTab === 'direct') {
    displayData =
      searchRecent && globalSearchTerm && subChatActiveTab === 'direct'
        ? filterByTab(searchRecent?.chats)
        : visibleRecentChats
  }

  const currentUser = currentUserData?.user
  const activeChat = selectedChat?.id || null
  const togglePinConversation = mutations.useTogglePinConversation()

  const handlePined = async (user: RecentChat, e: React.MouseEvent) => {
    e.stopPropagation()
    togglePinConversation.mutate(
      { targetId: user.chat_id, type: user.isAnnouncement ? 'announcement' : user.chat_type },
      {
        onSuccess: (data) => {
          dispatch(
            togglePinChat({
              id: user.chat_id,
              type: user.chat_type,
              pinned: Boolean(data?.pinned),
              pinned_at: data?.pinned_at ?? null,
            }),
          )
        },
      },
    )
    setPinedMenu((prev) => ({
      ...prev,
      [user.chat_id]: !prev[user.chat_id],
    }))
  }

  useEffect(() => {
    if (recentChatsList.length > 0) {
      // Calculate actual unread count from the chat list
      const totalUnread = recentChatsList.reduce((total: number, chat: RecentChat) => {
        return total + (chat.unreadCount || 0)
      }, 0)
      count.current = totalUnread
      dispatch(setIsUnread(totalUnread))
    } else {
      count.current = 0
      dispatch(setIsUnread(0))
    }
  }, [recentChatsList])

  // const handleUnread = (contact: any) => {
  //   if (!contact) return
  //   // Recalculate unread count after selecting a chat
  //   // The backend should handle marking messages as read
  //   // We'll recalculate on next recentChatsList update
  // }

  const changeChatClick = (contact: RecentChat) => {
    dispatch(
      selectChat({
        id: contact.chat_id,
        type: contact.chat_type,
        pinned: contact.isPinned,
      }),
    )

    dispatch(setSelectedUser(contact))
    dispatch(setMobileMenu())
    dispatch(setOnSelect(true))
    // Removed handleUnread call - unread count is now calculated from recentChatsList
    // if (profileSidebarWidth <= 800) {
    //   dispatch(openCloseSidebar(true))
    // } else {
    //   if (!sidebarToggle) {
    //     dispatch(openCloseSidebar(false))
    //   }
    // }
    onChatSelect?.()

    if (
      (selectedUser?.chat_id !== contact.chat_id ||
        selectedUser?.chat_type !== contact.chat_type ||
        !chatPinVerified) &&
      contact.isLocked
    ) {
      dispatch(setSelectedChatMessages([]))
      dispatch(setChatPinVerified(null))
      queryClient.removeQueries({
        queryKey: [KEYS.GET_MESSAGES, contact.chat_id, contact.chat_type],
      })
      setLockOpen(true)
    }
  }

  if (recentChatsLoading) {
    return <RecentChatsSkeleton />
  }

  const handleAddFriend = () => {
    dispatch(setMainSidebarActiveTab('friend-suggestions'))
    const screen = TAB_TO_SCREEN_MAP['friend-suggestions']
    if (screen) {
      dispatch(setScreen(screen))
    }
    if (profileSidebarWidth <= 800) {
      dispatch(openCloseSidebar())
    }
  }

  const clearLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }

  const handleLongPressStart = (contact: RecentChat) => {
    longPressTriggeredRef.current = false
    clearLongPress()
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true
      setActiveDropdown(contact)
    }, LONG_PRESS_DURATION)
  }

  const handleLongPressEnd = () => {
    clearLongPress()
  }

  const getEmptyStateMessage = () => {
    if (localSearch) {
      return {
        title: 'No matching contacts',
        description: 'Try a different search term',
      }
    }

    switch (subRecentActiveTab) {
      case 'unread':
        return {
          title: 'No unread messages',
          description: 'All caught up! You have no unread conversations.',
        }
      case 'favourite':
        return {
          title: 'No favorite contacts',
          description: 'Mark contacts as favorite to see them here',
        }
      case 'group':
        return {
          title: 'No groups chats',
          description: 'Group conversations will appear here',
        }
      default:
        return {
          title: 'No contacts yet',
          description: 'Add friends to start chatting',
        }
    }
  }

  if (displayData?.length === 0) {
    const emptyState = getEmptyStateMessage()
    return (
      <div className="empty-state recent-chat-empty text-center">
        <div className="mb-2">
          <Image src={`${ImagePath}/gif/download.gif`} />
        </div>
        <h5>{emptyState.title}</h5>
        <p className="text-muted">{emptyState.description}</p>
        {subRecentActiveTab === 'direct' && !localSearch && (
          <Button color="primary" className="d-flex mx-auto px-3 py-2" onClick={handleAddFriend}>
            <Plus />
            Add Friends
          </Button>
        )}
      </div>
    )
  }

  return (
    <>
      <ul className="chat-main custom-scroll">
        {displayData?.map((contact) => {
          return (
            <li
              className={`${
                activeChat === contact.chat_id && selectedChat?.type === contact.chat_type ? 'active' : ''
              } ${contact?.isPinned ? 'pined' : ''}`}
              key={`${contact.chat_id}_${contact.chat_type}`}
              onClick={() => {
                // Guard 1: long-press just fired — don't also navigate
                if (longPressTriggeredRef.current) {
                  longPressTriggeredRef.current = false
                  return
                }
                // Guard 2: menu is open — close it instead of navigating
                // Guard 3: iOS ghost-click window after a menu tap
                if (activeDropdown || Date.now() < ignoreRowClickUntilRef.current) {
                  if (activeDropdown) setActiveDropdown(null)
                  return
                }
                changeChatClick(contact)
              }}
              onMouseDown={() => handleLongPressStart(contact)}
              onMouseUp={handleLongPressEnd}
              onMouseLeave={handleLongPressEnd}
              onTouchStart={() => handleLongPressStart(contact)}
              onTouchEnd={handleLongPressEnd}
              onTouchCancel={handleLongPressEnd}
            >
              <div className="chat-box">
                {!contact.isBroadcast ? (
                  <PresenceProfile
                    userId={contact.chat_type !== 'group' && !contact.isAnnouncement ? contact?.chat_id : null}
                  >
                    <ChatAvatar
                      data={contact}
                      name={{ name: contact.isAnnouncement ? app_name : contact.name?.firstTwoWords() }}
                      customClass="user-info avatar-sm"
                    />
                  </PresenceProfile>
                ) : (
                  <div
                    className={`profile chatavtar-div d-flex justify-content-center align-items-center main-broadcast`}
                  >
                    <SvgIcon iconId="broadcast" />
                  </div>
                )}
                <div className="details">
                  <div className="d-flex align-items-center">
                    <h6>{contact.isAnnouncement ? app_name : contact.name?.firstTwoWords() || 'Unknown User'}</h6>
                    {(contact.isAnnouncement ||
                      (contact.chat_type !== 'group' &&
                        (contact.is_verified || Boolean(contact?.sender?.is_verified)))) && (
                      <SvgIcon iconId="blue-tick" className="ms-1 verified-tick" />
                    )}
                  </div>
                  <p
                    className={
                      !contact.isBlocked && contact.unreadCount > 0 && contact.chat_id !== currentUser?.id
                        ? 'font-primary'
                        : ''
                    }
                  >
                    {contact.lastMessage &&
                      !contact.isBroadcast &&
                      contact.lastMessage.sender_id === currentUser?.id &&
                      contact.lastMessage.message_type !== 'system' &&
                      (() => {
                        const isGroup = contact.chat_type === 'group'
                        let statuses: any[] = []
                        const rawStatuses = contact.lastMessage.statuses

                        if (rawStatuses) {
                          if (Array.isArray(rawStatuses)) {
                            statuses = rawStatuses
                          } else if (typeof rawStatuses === 'string') {
                            try {
                              const parsed = JSON.parse(rawStatuses)
                              statuses = Array.isArray(parsed) ? parsed : parsed ? [parsed] : []
                            } catch {
                              statuses = []
                            }
                          } else if (typeof rawStatuses === 'object' && rawStatuses !== null) {
                            statuses = [rawStatuses]
                          }
                        }

                        if (statuses.length === 0) {
                          return <SvgIcon className="sent" iconId="right" />
                        }

                        if (!isGroup) {
                          const hasSeen = statuses.some((s) => s?.status === 'seen' || s?.status === 'read')
                          const hasDelivered = hasSeen || statuses.some((s) => s?.status === 'delivered')

                          if (hasSeen) return <SvgIcon className="seen" iconId="right-2" />
                          if (hasDelivered) return <SvgIcon className="delivered" iconId="right-2" />
                          return <SvgIcon className="sent" iconId="right" />
                        }

                        const allSeen = statuses.every((s) => s?.status === 'seen' || s?.status === 'read')
                        const allDelivered =
                          allSeen || statuses.every((s) => ['delivered', 'seen', 'read'].includes(s?.status))

                        if (allSeen) return <SvgIcon className="seen" iconId="right-2" />
                        if (allDelivered) return <SvgIcon className="delivered" iconId="right-2" />
                        return <SvgIcon className="sent" iconId="right" />
                      })()}{' '}
                    <DecryptedMessagePreview
                      className={`recent-chat-content ${!contact.isBlocked && contact.unreadCount > 0 ? 'unread-last-message' : ''}`}
                      message={contact.lastMessage}
                      fallback={contact?.lastMessage?.metadata?.default_content || 'No messages yet'}
                      maxLength={35}
                    />
                  </p>
                </div>
                <div className="date-status">
                  <div className="badge-aligns">
                    {contact.isPinned && (
                      <i className="ti-pin2 pinned-icon" onClick={(e) => handlePined(contact, e)}>
                        <svg
                          fill="#0a66c2"
                          width="800px"
                          height="800px"
                          viewBox="0 0 32 32"
                          version="1.1"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M31.714 11.864l-11.505-11.563c-0.248-0.249-0.605-0.35-0.948-0.266-0.341 0.083-0.613 0.339-0.717 0.674-0.692 2.228-0.773 4.245-0.244 6.084-0.049 0.034-0.095 0.070-0.138 0.113l-5.347 5.346c-1.725-0.8-3.579-1.233-5.428-1.233-1.175 0-2.327 0.174-3.424 0.515-0.334 0.104-0.59 0.375-0.674 0.714s0.014 0.698 0.261 0.947l6.843 6.887-9.568 9.72-0.832 2.192 2.011-0.777 9.793-9.72 6.932 6.977c0.189 0.192 0.447 0.295 0.709 0.295 0.079 0 0.159-0.010 0.238-0.029 0.341-0.084 0.613-0.34 0.717-0.675 0.905-2.913 0.64-6.042-0.636-8.848l5.459-5.46c0.020-0.020 0.033-0.041 0.051-0.063 0.824 0.236 1.678 0.361 2.564 0.361 1.101 0 2.268-0.158 3.468-0.531 0.334-0.104 0.59-0.375 0.674-0.714s-0.015-0.697-0.262-0.945zM18.849 25.755l-12.587-12.669c3.23-0.377 6.714 0.925 9.236 3.447 2.51 2.509 3.735 5.978 3.351 9.221zM18.757 17.392c-0.526-0.804-1.14-1.568-1.845-2.274-0.702-0.702-1.469-1.321-2.28-1.854l4.504-4.503c0.459 0.799 1.052 1.563 1.782 2.291 0.745 0.745 1.534 1.348 2.363 1.814zM22.332 9.639c-1.923-1.923-2.664-4.067-2.271-6.653l8.966 9.012c-2.583 0.37-4.738-0.403-6.695-2.36z"></path>
                        </svg>
                      </i>
                    )}
                    {contact.isMuted && (
                      <span className="badge-aligns-content" title="Muted">
                        <SvgIcon className="stroke-primary muted-icon" iconId="muted-volume" />
                      </span>
                    )}
                    {!contact.isBlocked && contact.unreadCount > 0 && (
                      <Badge color={contact.is_unread_mentions ? 'danger' : 'primary'} className="sm">
                        {contact.unreadCount > 9 ? '9+' : contact.unreadCount}
                      </Badge>
                    )}
                    {/* Announcement/Admin chats are read-only, so the quick-action
                        menu (Favorite/Pin/Mute/Archive) and its toggle arrow don't
                        apply — hide them. */}
                    {!contact.isAnnouncement && (
                      <RecentQuickAction
                        contact={contact}
                        isDropdownOpen={
                          activeDropdown?.chat_id === contact.chat_id && activeDropdown?.chat_type === contact.chat_type
                        }
                        onToggleDropdown={(isOpen) => {
                          ignoreRowClickUntilRef.current = Date.now() + 600
                          setActiveDropdown(isOpen ? contact : null)
                        }}
                        onPinToggle={(e) => handlePined(contact, e)}
                        activeDropdown={activeDropdown}
                      />
                    )}
                  </div>
                  {contact?.lastMessage?.created_at && (
                    <p> {formatTime(contact?.lastMessage?.created_at, { hour12: use12HourFormat })}</p>
                  )}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
      <LockChatModal
        isOpen={lockOpen}
        onClose={() => {
          setLockOpen(false)
        }}
        actionType="PinVerified"
      />
    </>
  )
}

export default RecentChats
